import type { Cita } from '../../api/ops/citas';
import type { Evento } from '../../api/ops/eventos';
import type { CifrasIndicador, Indicador } from '../../api/ops/indicadores';
import type { ResumenLecturas } from '../../api/ops/pases';
import type { Reserva } from '../../api/ops/reservas';
import type { Navegacion } from '../../api/operaciones';
import { enlaceDeReferencia } from '../../logica/navegacion';
import { enOrdenDelDia } from '../indicadores/reglas';

/**
 * «Lo próximo» de Inicio (7.1, `proximo()` de `diseno/analitica/7.1-inicio.html`), sin estilos: el evento de hoy ya
 * empezado (llegadas por hora según la puerta, `getScanSummary.by_hour`, y entradas vendidas sobre el aforo) o, sin
 * evento, lo de hoy por hora: las reservas (`listReservations` de la jornada) o las citas (`listAppointments` de hoy) con
 * sus llegadas. Solo se agrupa por hora lo que da la lista; las cifras de verdad las da su módulo.
 */

export interface Barra {
    etiqueta: string;
    detalle: string;
    valor: number;
}

export interface Anillo {
    titulo: string;
    sub: string;
    valor: number;
    tope: number;
    etiqueta: string;
    nota: string;
}

export interface Proximo {
    tipo: 'evento' | 'reservas' | 'citas';
    titulo: string;
    cifra: number;
    /** «de 143» junto a la cifra (solo el evento). */
    deCuantos: string;
    linea: string;
    barras: Barra[];
    /** Barra de la hora en curso, en rojo; −1 si no hay. */
    ahora: number;
    unidad: string;
    vacio: string;
    porque: string;
    /**
     * «Ver …»: la entrada del menú del módulo dueño (`module` de getMyNavigation) con lo que abre o, con `destino`, el
     * destino de enlace que declara el módulo (`link_targets`, G862); sin entrada o sin destino, sin botón.
     */
    verMas: { texto: string; modulo: string; consulta: string; destino?: { tipo: string; id: string } };
    anillo: Anillo | null;
}

const NO_CUENTAN_RESERVA: readonly Reserva['status'][] = ['cancelled', 'expired', 'refunded', 'pending_payment'];
const NO_CUENTAN_CITA: readonly Cita['status'][] = ['cancelled', 'expired', 'pending_payment'];

const dosCifras = (n: number): string => String(n).padStart(2, '0');
const horaDe = (hhmm: string): number => Number(hhmm.slice(0, 2));
const corta = (hhmm: string): string => hhmm.slice(0, 5);

/** La ruta de la entrada principal del menú de un módulo (la de menor orden, sin madre); null si la persona no la tiene. */
export function rutaDelModulo(items: readonly { module: string; path: string; area: string; order: number; parent_key?: string | null; not_contracted?: boolean }[], modulo: string, consulta = ''): string | null {
    const e = items.filter((i) => i.module === modulo && i.area === 'menu' && (i.parent_key ?? null) === null && i.not_contracted !== true).sort((a, b) => a.order - b.order)[0];
    if (e === undefined) {
        return null;
    }
    return consulta === '' ? e.path : `${e.path}${e.path.includes('?') ? '&' : '?'}${consulta}`;
}

/** Adónde lleva «Ver …»: el destino de enlace si lo trae (G862) o la entrada principal del módulo. */
export function rutaDeVerMas(nav: Pick<Navegacion, 'items' | 'link_targets'>, verMas: Proximo['verMas']): string | null {
    if (verMas.destino !== undefined) {
        return enlaceDeReferencia(nav.link_targets, verMas.destino.tipo, verMas.destino.id)?.ruta ?? null;
    }
    return rutaDelModulo(nav.items, verMas.modulo, verMas.consulta);
}

/** Cuántas hay en cada hora, de la primera a la última (si cruza la medianoche, sigue en orden: 23 · 0 · 1). */
export function barrasPorHora(horas: readonly number[]): Barra[] {
    if (horas.length === 0) {
        return [];
    }
    return enOrdenDelDia(horas).map((h) => ({ etiqueta: String(h), detalle: `De ${dosCifras(h)}:00 a ${dosCifras((h + 1) % 24)}:00`, valor: horas.filter((x) => x === h).length }));
}

/** La hora de ahora en la zona del negocio (0–23). */
export function horaLocal(ahora: Date, zona: string): number {
    try {
        return Number(new Intl.DateTimeFormat('es-ES', { hour: '2-digit', hourCycle: 'h23', timeZone: zona }).format(ahora));
    } catch {
        return ahora.getHours();
    }
}

const indiceAhora = (barras: readonly Barra[], hora: number): number => barras.findIndex((b) => b.etiqueta === String(hora));

/** El evento publicado que ya ha empezado y no ha terminado (el que empezó más tarde, si hay varios). */
export function eventoEnCurso(eventos: readonly Evento[], ahora: Date): Evento | null {
    const t = ahora.getTime();
    return (
        eventos
            .filter((e) => e.status === 'published' && e.starts_at !== undefined && e.ends_at !== undefined && new Date(e.starts_at).getTime() <= t && new Date(e.ends_at).getTime() > t)
            .sort((a, b) => (b.starts_at ?? '').localeCompare(a.starts_at ?? ''))[0] ?? null
    );
}

/** El evento de hoy ya empezado: lo que ha pasado en la puerta, por hora, y lo vendido sobre el aforo. */
export function proximoDeEvento(evento: Evento, lecturas: ResumenLecturas | null, ahora: Date, zona: string): Proximo {
    const porHora = (lecturas?.by_hour ?? []).map((h) => ({ hora: horaLocal(new Date(h.hour), zona), llegadas: h.arrivals }));
    const barras = enOrdenDelDia(porHora.map((h) => h.hora)).map((h) => ({
        etiqueta: dosCifras(h),
        detalle: `De ${dosCifras(h)}:00 a ${dosCifras((h + 1) % 24)}:00`,
        valor: porHora.filter((x) => x.hora === h).reduce((s, x) => s + x.llegadas, 0),
    }));
    const aforo = evento.capacity_detail;
    const vendidas = aforo.tickets_confirmed;
    return {
        tipo: 'evento',
        titulo: `${evento.name ?? ''} · ya han entrado`,
        cifra: lecturas?.totals.arrivals ?? 0,
        deCuantos: '',
        linea: 'Por hora, según la puerta',
        barras,
        ahora: barras.findIndex((b) => b.etiqueta === dosCifras(horaLocal(ahora, zona))),
        unidad: 'personas',
        vacio: 'Todavía no ha entrado nadie',
        porque: 'Las llegadas salen aquí en cuanto la puerta lee la primera entrada.',
        // G862: el destino `evento` de Eventos; Inicio no compone la ruta.
        verMas: { texto: 'Ver el evento', modulo: 'entradas', consulta: '', destino: { tipo: 'evento', id: evento.id } },
        anillo:
            aforo.capacity > 0
                ? { titulo: 'Entradas vendidas', sub: `${evento.name ?? ''} · aforo de ${String(aforo.capacity)}`, valor: vendidas, tope: aforo.capacity, etiqueta: 'Vendidas', nota: `Quedan ${String(aforo.available)} libres` }
                : null,
    };
}

/** Las reservas de hoy por hora, con las personas y la primera. */
/**
 * «Plazas de hoy» (G864, ADR-0220): el indicador del catálogo con el papel `occupancy` (sin escribir su código). Sin
 * indicador con ese papel (o sin permiso para leerlo, que no sale en el catálogo), nada.
 */
export function indicadorDePlazas(catalogo: readonly Pick<Indicador, 'code' | 'role'>[]): string | null {
    return catalogo.find((i) => i.role === 'occupancy')?.code ?? null;
}

/** El anillo de «Plazas de hoy» con las plazas reservadas (numerador) sobre las abiertas (denominador) que da la API. */
export function anilloDePlazas(cifras: CifrasIndicador | null): Anillo | null {
    const total = (cifras?.series.find((s) => s.dimension_value == null) ?? cifras?.series[0])?.total;
    const valor = total?.numerator;
    const tope = total?.denominator;
    if (valor == null || tope == null || tope <= 0) {
        return null;
    }
    const quedan = Math.max(tope - valor, 0);
    return { titulo: 'Plazas de hoy', sub: 'Personas apuntadas de las plazas que hay', valor, tope, etiqueta: 'Ocupadas', nota: quedan === 1 ? 'Queda 1' : `Quedan ${String(quedan)}` };
}

export function proximoDeReservas(reservas: readonly Reserva[], ahora: Date, zona: string, plazas: Anillo | null = null): Proximo {
    const validas = reservas.filter((r) => !NO_CUENTAN_RESERVA.includes(r.status));
    const barras = barrasPorHora(validas.map((r) => (r.local_time == null ? horaLocal(new Date(r.starts_at), zona) : horaDe(r.local_time))));
    const personas = validas.reduce((s, r) => s + r.party_size, 0);
    const primera = [...validas].sort((a, b) => a.starts_at.localeCompare(b.starts_at))[0];
    return {
        tipo: 'reservas',
        titulo: 'Reservas de hoy',
        cifra: validas.length,
        deCuantos: '',
        linea: validas.length === 0 ? 'Por hora' : `Por hora · ${String(personas)} ${personas === 1 ? 'persona' : 'personas'} · la primera a las ${corta(primera?.local_time ?? '')}`,
        barras,
        ahora: indiceAhora(barras, horaLocal(ahora, zona)),
        unidad: 'reservas',
        vacio: 'Hoy no hay reservas',
        porque: 'Cuando entre una, la verás aquí al momento.',
        verMas: { texto: 'Ver las reservas', modulo: 'reservas', consulta: '' },
        anillo: plazas,
    };
}

/** Las citas de hoy por hora, con las llegadas sobre las citas de hoy (no hay % de profesionales, pregunta 297). */
export function proximoDeCitas(citas: readonly Cita[], ahora: Date, zona: string, soloLasMias: boolean): Proximo {
    const validas = citas.filter((c) => !NO_CUENTAN_CITA.includes(c.status)).sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    const barras = barrasPorHora(validas.map((c) => horaDe(c.local_time)));
    const han = validas.filter((c) => c.arrived_at != null || c.status === 'arrived' || c.status === 'completed').length;
    const siguiente = validas.find((c) => new Date(c.starts_at).getTime() > ahora.getTime() && c.status === 'confirmed');
    const primera = validas[0];
    return {
        tipo: 'citas',
        titulo: soloLasMias ? 'Tus citas de hoy' : 'Citas de hoy',
        cifra: validas.length,
        deCuantos: '',
        linea: siguiente !== undefined ? `Por hora · la siguiente a las ${corta(siguiente.local_time)}` : primera !== undefined ? `Por hora · la primera a las ${corta(primera.local_time)}` : 'Por hora',
        barras,
        ahora: indiceAhora(barras, horaLocal(ahora, zona)),
        unidad: 'citas',
        vacio: 'Hoy no hay citas',
        porque: 'Cuando entre una, la verás aquí al momento.',
        verMas: { texto: soloLasMias ? 'Ver mi agenda' : 'Ver la agenda', modulo: 'citas', consulta: '' },
        anillo:
            validas.length > 0
                ? {
                      titulo: soloLasMias ? 'Tus llegadas de hoy' : 'Llegadas de hoy',
                      sub: soloLasMias ? 'Clientes que ya han venido a tus citas' : 'Clientes que ya han venido',
                      valor: han,
                      tope: validas.length,
                      etiqueta: 'Han venido',
                      nota: siguiente !== undefined ? `La siguiente, a las ${corta(siguiente.local_time)}` : `Quedan ${String(validas.length - han)} por venir`,
                  }
                : null,
    };
}
